import * as React from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { Check, ChevronsUpDown, Loader2, Plus } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

const triggerClassName = cn(
  'field-glass flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-input bg-[hsl(var(--glass-bg)/0.5)] px-3.5 py-2 shadow-glass-sm backdrop-blur-sm',
  'transition-[box-shadow,border-color,background-color] duration-200',
  'focus:outline-none focus:ring-2 focus:ring-ring/40 focus:ring-offset-0',
  'disabled:cursor-not-allowed disabled:opacity-50',
);

const itemClassName = cn(
  'relative flex cursor-default select-none items-center gap-2 rounded-lg py-2 pl-8 pr-2 text-sm outline-none',
  'data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground',
);

export interface ComboboxOption {
  value: string;
  label: string;
  /** Linha secundária (ex.: preço do modelo). Também entra na busca. */
  description?: string;
}

interface ComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: ComboboxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  id?: string;
}

/** Seleção com busca (valor ≠ rótulo) — cmdk + Popover, para listas longas. */
export function Combobox({
  value,
  onChange,
  options,
  placeholder = 'Selecione…',
  searchPlaceholder = 'Buscar…',
  emptyLabel = 'Nenhum resultado.',
  disabled,
  loading,
  className,
  id,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const selected = options.find((option) => option.value === value);

  const term = query.trim().toLowerCase();
  const filtered = term
    ? options.filter((option) =>
        [option.label, option.value, option.description ?? ''].some((text) => text.toLowerCase().includes(term)),
      )
    : options;

  return (
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          className={cn(triggerClassName, className, 'text-[16px] sm:text-sm')}
        >
          <span className={cn('truncate', !selected && 'text-muted-foreground/70')}>
            {selected?.label ?? placeholder}
          </span>
          {loading ? (
            <Loader2 className="size-4 shrink-0 animate-spin opacity-60" />
          ) : (
            <ChevronsUpDown className="size-4 shrink-0 opacity-60" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-72 p-0">
        <CommandPrimitive shouldFilter={false}>
          <div className="border-b border-border/60 px-3">
            <CommandPrimitive.Input
              value={query}
              onValueChange={setQuery}
              placeholder={searchPlaceholder}
              className="flex h-10 w-full bg-transparent py-2 text-[16px] outline-none placeholder:text-muted-foreground/70 sm:text-sm"
            />
          </div>
          <CommandPrimitive.List className="max-h-64 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <CommandPrimitive.Empty className="px-3 py-2 text-sm text-muted-foreground">
                {emptyLabel}
              </CommandPrimitive.Empty>
            ) : null}
            {filtered.map((option) => (
              <CommandPrimitive.Item
                key={option.value}
                value={option.value}
                onSelect={() => {
                  onChange(option.value);
                  setOpen(false);
                  setQuery('');
                }}
                className={itemClassName}
              >
                <span className="absolute left-2 flex size-3.5 items-center justify-center">
                  {option.value === value ? <Check className="size-4 text-primary" /> : null}
                </span>
                <span className="min-w-0">
                  <span className="block truncate">{option.label}</span>
                  {option.description ? (
                    <span className="block truncate text-xs text-muted-foreground">{option.description}</span>
                  ) : null}
                </span>
              </CommandPrimitive.Item>
            ))}
          </CommandPrimitive.List>
        </CommandPrimitive>
      </PopoverContent>
    </Popover>
  );
}

interface CreatableComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  /** Quando presente, mostra "Criar '<texto>'" pra opções que ainda não existem no catálogo. */
  onCreate?: (label: string) => void | Promise<void>;
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

/** Autocomplete com criação inline — cmdk + Popover, sem depender de um catálogo pré-carregado inteiro na tela. */
export function CreatableCombobox({
  value,
  onChange,
  options,
  onCreate,
  placeholder = 'Selecione…',
  emptyLabel = 'Nenhum resultado.',
  disabled,
  loading,
  className,
}: CreatableComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [creating, setCreating] = React.useState(false);

  const normalizedQuery = query.trim();
  const hasExactMatch = options.some((o) => o.toLowerCase() === normalizedQuery.toLowerCase());
  const filteredOptions = options.filter(
    (o) => !normalizedQuery || o.toLowerCase().includes(normalizedQuery.toLowerCase()),
  );

  const select = (next: string) => {
    onChange(next);
    setQuery('');
    setOpen(false);
  };

  const create = async () => {
    if (!normalizedQuery || !onCreate) return;
    setCreating(true);
    try {
      await onCreate(normalizedQuery);
      select(normalizedQuery);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(triggerClassName, className, 'text-[16px] sm:text-sm')}
        >
          <span className={cn('truncate', !value && 'text-muted-foreground/70')}>{value || placeholder}</span>
          {loading ? (
            <Loader2 className="size-4 shrink-0 animate-spin opacity-60" />
          ) : (
            <ChevronsUpDown className="size-4 shrink-0 opacity-60" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
        <CommandPrimitive shouldFilter={false}>
          <div className="border-b border-border/60 px-3">
            <CommandPrimitive.Input
              value={query}
              onValueChange={setQuery}
              placeholder="Buscar ou criar…"
              className="flex h-10 w-full bg-transparent py-2 text-[16px] outline-none placeholder:text-muted-foreground/70 sm:text-sm"
            />
          </div>
          <CommandPrimitive.List className="max-h-56 overflow-y-auto p-1">
            {filteredOptions.length === 0 && !(onCreate && normalizedQuery && !hasExactMatch) ? (
              <CommandPrimitive.Empty className="px-3 py-2 text-sm text-muted-foreground">
                {emptyLabel}
              </CommandPrimitive.Empty>
            ) : null}
            {filteredOptions.map((option) => (
              <CommandPrimitive.Item
                key={option}
                value={option}
                onSelect={() => select(option)}
                className={itemClassName}
              >
                <span className="absolute left-2 flex size-3.5 items-center justify-center">
                  {option === value ? <Check className="size-4 text-primary" /> : null}
                </span>
                {option}
              </CommandPrimitive.Item>
            ))}
            {onCreate && normalizedQuery && !hasExactMatch ? (
              <CommandPrimitive.Item
                value={`__create__${normalizedQuery}`}
                onSelect={() => void create()}
                disabled={creating}
                className="relative flex cursor-default select-none items-center gap-2 rounded-lg py-2 pl-8 pr-2 text-sm text-primary outline-none data-[selected=true]:bg-accent"
              >
                {creating ? (
                  <Loader2 className="absolute left-2 size-3.5 animate-spin" />
                ) : (
                  <Plus className="absolute left-2 size-3.5" />
                )}
                Criar “{normalizedQuery}”
              </CommandPrimitive.Item>
            ) : null}
          </CommandPrimitive.List>
        </CommandPrimitive>
      </PopoverContent>
    </Popover>
  );
}
